import axiosConfig from "./axiosConfig";

const MIME_CANDIDATES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
];

const WIDTH = 1280;
const HEIGHT = 720;
const FPS = 15;
const VIDEO_BITS = 1200000;
const AUDIO_BITS = 48000;
const CHUNK_MS = 5000;
const MAX_DURATION_MS = 60 * 60 * 1000;
const UPLOAD_RETRIES = 3;
const TERMINAL_CODES = [
  "E_RECORD_TOO_LARGE",
  "E_RECORD_CLOSED",
  "E_RECORD_NOT_FOUND",
  "E_RECORD_STORAGE",
];
const INSET_SIZE = 180;
const INSET_MARGIN = 32;

// Safari writes MP4 only, which cannot be appended chunk by chunk the way
// WebM can: no supported type means the feature stays hidden.
export const getSupportedMime = () => {
  if (typeof window.MediaRecorder === "undefined") return null;
  return (
    MIME_CANDIDATES.find((mime) => window.MediaRecorder.isTypeSupported(mime)) ||
    null
  );
};

export const isRecordingSupported = () => getSupportedMime() !== null;

const contain = (sourceWidth, sourceHeight, boxWidth, boxHeight) => {
  const scale = Math.min(boxWidth / sourceWidth, boxHeight / sourceHeight);
  const width = sourceWidth * scale;
  const height = sourceHeight * scale;
  return {
    x: (boxWidth - width) / 2,
    y: (boxHeight - height) / 2,
    width,
    height,
  };
};

const cover = (sourceWidth, sourceHeight, boxWidth, boxHeight) => {
  const scale = Math.max(boxWidth / sourceWidth, boxHeight / sourceHeight);
  const width = sourceWidth * scale;
  const height = sourceHeight * scale;
  return {
    x: (boxWidth - width) / 2,
    y: (boxHeight - height) / 2,
    width,
    height,
  };
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const createSessionRecorder = ({
  remoteVideo,
  localVideo,
  markerCanvas,
  folderToken,
  onError = () => {},
  onAutoStop = () => {},
}) => {
  let recordToken = null;
  let recorder = null;
  let audioContext = null;
  let canvasStream = null;
  let drawTimer = null;
  let maxTimer = null;
  let startedAt = null;
  let stopped = false;
  let seq = 0;
  let queue = Promise.resolve();
  let errored = false;

  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const context = canvas.getContext("2d");

  const drawFrame = () => {
    context.fillStyle = "#000000";
    context.fillRect(0, 0, WIDTH, HEIGHT);

    if (remoteVideo && remoteVideo.videoWidth) {
      const box = contain(
        remoteVideo.videoWidth,
        remoteVideo.videoHeight,
        WIDTH,
        HEIGHT
      );
      context.drawImage(remoteVideo, box.x, box.y, box.width, box.height);

      if (markerCanvas && markerCanvas.width && markerCanvas.height) {
        context.drawImage(markerCanvas, box.x, box.y, box.width, box.height);
      }
    }

    if (localVideo && localVideo.videoWidth) {
      const center = INSET_MARGIN + INSET_SIZE / 2;
      context.save();
      context.beginPath();
      context.arc(center, center, INSET_SIZE / 2, 0, Math.PI * 2);
      context.clip();
      const box = cover(
        localVideo.videoWidth,
        localVideo.videoHeight,
        INSET_SIZE,
        INSET_SIZE
      );
      context.drawImage(
        localVideo,
        INSET_MARGIN + box.x,
        INSET_MARGIN + box.y,
        box.width,
        box.height
      );
      context.restore();
      context.beginPath();
      context.arc(center, center, INSET_SIZE / 2, 0, Math.PI * 2);
      context.strokeStyle = "rgba(255, 255, 255, 0.4)";
      context.lineWidth = 3;
      context.stroke();
    }
  };

  const buildAudioTrack = () => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;

    audioContext = new AudioContextClass();
    const destination = audioContext.createMediaStreamDestination();
    let connected = 0;

    [localVideo, remoteVideo].forEach((element) => {
      const stream = element && element.srcObject;
      if (!stream) return;
      const track = stream.getAudioTracks()[0];
      if (!track) return;
      audioContext
        .createMediaStreamSource(new MediaStream([track]))
        .connect(destination);
      connected++;
    });

    return connected ? destination.stream.getAudioTracks()[0] : null;
  };

  const fail = (code) => {
    if (errored) return false;
    errored = true;
    onError(code);
    return false;
  };

  const uploadChunk = async (chunkSeq, blob) => {
    if (errored) return false;

    for (let attempt = 0; attempt <= UPLOAD_RETRIES; attempt++) {
      try {
        const res = await axiosConfig(
          `folder/append-record?record=${recordToken}&seq=${chunkSeq}`,
          {
            data: blob,
            headers: { "Content-Type": "application/octet-stream" },
          }
        );
        if (res.data.success) return true;
      } catch (error) {
        // The server answers a refused chunk with 409, so axios throws here.
        const data = error.response && error.response.data;
        if (data && TERMINAL_CODES.includes(data.code)) return fail(data.code);
        // A gap or a network failure retries; the sequence guard makes a
        // replayed chunk harmless.
      }
      if (attempt < UPLOAD_RETRIES) await delay(1000 * (attempt + 1));
    }

    return fail("E_RECORD_UPLOAD");
  };

  const enqueue = (blob) => {
    const chunkSeq = seq++;
    queue = queue.then(() => uploadChunk(chunkSeq, blob));
  };

  const teardown = () => {
    if (drawTimer) clearInterval(drawTimer);
    if (maxTimer) clearTimeout(maxTimer);
    drawTimer = null;
    maxTimer = null;
    if (canvasStream) canvasStream.getTracks().forEach((t) => t.stop());
    canvasStream = null;
    if (audioContext) audioContext.close().catch(() => {});
    audioContext = null;
  };

  return {
    start(callback) {
      const mime = getSupportedMime();

      if (!mime) return callback({ success: false, code: "E_RECORD_MIME" });

      axiosConfig("folder/start-record", { data: { folderToken, mime } })
        .then((res) => {
          if (!res.data.success) {
            return callback({
              success: false,
              code: res.data.code || "E_RECORD_START",
            });
          }

          recordToken = res.data.recordToken;

          try {
            drawFrame();
            // setInterval rather than requestAnimationFrame: rAF stops entirely
            // in a background tab, which would freeze the recorded video.
            drawTimer = setInterval(drawFrame, 1000 / FPS);
            canvasStream = canvas.captureStream(FPS);

            const tracks = [canvasStream.getVideoTracks()[0]];
            const audioTrack = buildAudioTrack();
            if (audioTrack) tracks.push(audioTrack);

            recorder = new window.MediaRecorder(new MediaStream(tracks), {
              mimeType: mime,
              videoBitsPerSecond: VIDEO_BITS,
              audioBitsPerSecond: AUDIO_BITS,
            });

            recorder.ondataavailable = (event) => {
              if (event.data && event.data.size) enqueue(event.data);
            };

            recorder.start(CHUNK_MS);
            startedAt = Date.now();
            maxTimer = setTimeout(() => onAutoStop(), MAX_DURATION_MS);

            callback({ success: true });
          } catch (error) {
            teardown();
            callback({ success: false, code: "E_RECORD_START" });
          }
        })
        .catch(() => callback({ success: false, code: "E_RECORD_START" }));
    },

    stop(callback = () => {}) {
      if (stopped) return callback({ success: true });
      stopped = true;

      const duration = startedAt
        ? Math.round((Date.now() - startedAt) / 1000)
        : 0;

      const finalize = () => {
        teardown();
        queue
          .then(() =>
            axiosConfig("folder/stop-record", {
              data: { recordToken, duration },
            })
          )
          .then((res) => callback({ success: res.data.success, duration }))
          .catch(() => callback({ success: false, duration }));
      };

      if (recorder && recorder.state !== "inactive") {
        recorder.onstop = finalize;
        recorder.stop();
      } else {
        finalize();
      }
    },

    getDuration() {
      return startedAt ? Math.round((Date.now() - startedAt) / 1000) : 0;
    },
  };
};
